import React, {ReactElement, createRef, RefObject, ReactNode} from 'react';
import {FilterMatchMode} from 'primereact/api';
import {Column, ColumnFilterElementTemplateOptions} from 'primereact/column';
import {Calendar} from 'primereact/calendar';
import MComponent from '../Toolkit/Components/Component';
import WithRouterProps from '../Toolkit/WithRouterProps';
import MPage, {MPageProps} from '../Toolkit/Components/Shell/Page';
import MTable, {MTableProps} from '../Toolkit/Components/Controls/Table';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import MButton from '../Toolkit/Components/Controls/Button';
import {MAllowIconName, MNewIconName, MOpenIconName} from '../Toolkit/Icons';
import {SafeDig, SafeDigListReply, ProjectReply} from '../Api/SafeDigTypes';
import WithRouter from '../Toolkit/WithRouter';
import StatusIndicator from './Components/StatusIndicator';
import skullLogo from './Images/Skull25.png';
import {ExcelIconName, DigStatusNames, GotoIconName} from './SafeDigConstants';
import {SafeDigClient} from '../Api/SafeDigsClients';
import ImportDialog from './Dialogs/ImportDialog';
import ExcelImport from './ExcelImport';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import MessageDialog from './Dialogs/MessageDialog';
import MMultiDropDown from '../Toolkit/Components/Controls/MultiDropDown';
import {MDropDownItem} from '../Toolkit/Components/Controls/DropDownOption';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import SafeDigAccessTokenProvider from '../Api/SafeDigAccessTokenProvider';
import {DataTable, DataTableExportFunctionParams} from "primereact/datatable";


type SafeDigRow = {
    data: SafeDig;
}

type Props = WithMsalProps & WithRouterProps & {};

/** */
type State = {
    page: MPageProps;
    table: MTableProps<SafeDigRow>;
    selectedRow?: SafeDigRow;
    safeDigStatusValues: MDropDownItem<number>[];
    projectList: string[];
    completed:SafeDigRow[]
    includeComplete: boolean
    includeLabel: string
}

class ListSafeDigsPage extends MComponent<Props, State> {
    private readonly m_safedigClient: NSwagAdapter<SafeDigClient>;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_importRef: RefObject<ImportDialog>;
    private readonly m_messageRef: RefObject<MessageDialog>;
    private readonly m_datatableRef: RefObject<DataTable>;

    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_safedigClient = new NSwagAdapter<SafeDigClient>(new SafeDigClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_SAFEDIGS_API_KEY!,
                new SafeDigAccessTokenProvider(this.props.msalContext))));
        this.m_importRef = createRef<ImportDialog>();
        this.m_messageRef = createRef<MessageDialog>();
        this.m_pageRef = createRef<MPage>();
        this.m_datatableRef = createRef<DataTable>();
        this.initializeState();

    }

    private includeComplete_Click()
    {
        if (this.state.includeComplete) {
            this.safeSetState((draft: State) => {
                draft.includeComplete = false;
            });
            this.safeSetState((draft: State) => {
                draft.includeLabel = "Include Complete";
            });
            this.safeSetState((draft: State) => {
                draft.table.rowData = draft.table.rowData.filter(x => x.data.digStatus === 1 || x.data.digStatus === 2
                    || x.data.digStatus === 3 || x.data.digStatus === 5
                    || (x.data.digStatus === 4 && x.data.expiryDate !== null && x.data.expiryDate! >= new Date()))
            });
        }
        if (!this.state.includeComplete) {
            this.safeSetState((draft: State) => {
                draft.includeComplete = true;
            });
            this.safeSetState((draft: State) => {
                draft.includeLabel = "Exclude Complete";
            });
            this.safeSetState((draft: State) => {
                draft.table.rowData.push(...draft.completed)
            });
        }
    }
    private bindMethods(): void {

        this.isOpenButtonDisabled = this.isOpenButtonDisabled.bind(this);
        this.new_Click = this.new_Click.bind(this);
        this.open_Click = this.open_Click.bind(this);
        this.import_Click = this.import_Click.bind(this);
        this.export_Click = this.export_Click.bind(this);
        this.notices_Click = this.notices_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.onImport = this.onImport.bind(this);
        this.getDigStatusFilterTemplate = this.getDigStatusFilterTemplate.bind(this);
        this.readProjectList = this.readProjectList.bind(this);
        this.includeComplete_Click = this.includeComplete_Click.bind(this);
        this.exportFunction = this.exportFunction.bind(this);
    }

    /** */
    private initializeState(): void {

        //  Page
        const page: MPageProps = {
            locked: false
        };

        //  Table
        const table: MTableProps<SafeDigRow> = {

            exportName: "SafeDigs",
            rowData: [],
            filters: {
                'data.estimate': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.requestor': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.county': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.postcode': {
                    value: null,
                    matchMode: FilterMatchMode.CONTAINS
                },
                'data.requiredDate': {
                    value: null,
                    matchMode: FilterMatchMode.DATE_IS
                },
                'data.receivedDate': {
                    value: null,
                    matchMode: FilterMatchMode.DATE_IS
                },
                'data.digStatus': {
                    value: null,
                    matchMode: FilterMatchMode.IN
                }
            }
        };

        const safeDigStatusValues: MDropDownItem<number>[] = DigStatusNames.map(
            (item: string, index: number) => {
                return {label: item, value: index};
            }
        );

        this.state = {
            page: page,
            table: table,
            selectedRow: undefined,
            safeDigStatusValues: safeDigStatusValues,
            projectList: [],
            completed: [],
            includeComplete: false,
            includeLabel: "Include Complete"
        };

    }

    private isOpenButtonDisabled(): boolean {
        return this.state.selectedRow === undefined;
    }

    /** */
    private new_Click(): void {
        this.props.navigate('/safedigs/create');
    }

    private open_Click(): void {
        this.props.navigate(`/safedigs/${this.state.selectedRow!.data.safeDigsId}`);
    }

    private exportFunction(e : DataTableExportFunctionParams){
        if(e.field === "data.project")
            return this.state.projectList[e.data - 1];
        if(e.field === "data.digStatus")
            return DigStatusNames[e.data]
        if(e.field === "data.uploadDate" || e.field === "data.receivedDate" || e.field === "data.requiredDate" || e.field === "data.supervisionDate" || e.field === "data.expiryDate")
            return new Intl.DateTimeFormat('en-GB').format(e.data)
        return e.data
    }

    private export_Click(): void {
        this.m_datatableRef.current?.exportCSV({selectionOnly: false})
    }

    private import_Click(): void {
        this.m_importRef.current!.show(this.onImport);
    }

    private async onImport(excelImporter: ExcelImport): Promise<void> {
        const userName = this.props.msalContext.accounts[0].name;

        const importCount = await excelImporter.ImportEntries(this.m_safedigClient, userName!);

        //Refresh data from DB.
        await this.page_onLoadData();

        const dialogContents: ReactNode = (
            <div className="formgrid grid">
                <p>{`${importCount} record${importCount === 1 ? ' has' : 's have'} been imported from the file.`}<br/><br/>
                    {`${excelImporter.ErrorMessages.length} ${excelImporter.ErrorMessages.length === 1 ? ' error has' : ' errors have'} been detected in the file.`}</p>
                {excelImporter.ErrorMessages.length > 0 && <div className="min-w-full" style={{borderStyle: 'solid', borderColor: 'red'}}>
                    <ul>
                        {excelImporter.ErrorMessages.map((item, key) => {
                            return <li key={key}>{item}</li>;
                        })}
                    </ul>
                </div>
                }
            </div>
        );

        this.m_messageRef.current!.show('Import Complete', dialogContents, {name: ExcelIconName, color: 'mcolor-info'});
    }

    private notices_Click(): void {
        this.props.navigate(`/notices`);
    }

    private table_onSelectionChange(selection: SafeDigRow | SafeDigRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as SafeDigRow | undefined);

    }

    private async page_onLoadData(): Promise<void> {

        const listResult = await this.m_safedigClient.callWithReply(
            SafeDigListReply, (client) => client.getAll(
            ));

        listResult.throwIfFailed();

        this.safeSetState((draft: State) => {

            draft.table.rowData = listResult.reply!.data
                                                   .filter(x => x.digStatus === 1 || x.digStatus === 2 || x.digStatus === 3 || x.digStatus === 5
                                                       || (x.digStatus === 4 && x.expiryDate !== null && x.expiryDate! >= new Date()))
                                                   .map((w: SafeDig) => {
                return {data: w} as SafeDigRow;
            });
            draft.completed = listResult.reply!.data.filter(x => x.digStatus === 6 || (x.digStatus === 4 && (x.expiryDate === null || x.expiryDate! < new Date())))
                                               .map((w: SafeDig) => {
                return {data: w} as SafeDigRow;
            });
        });

        await this.readProjectList();
    }

    private async readProjectList() {
        const projectResult = await this.m_safedigClient.callWithReply(
            ProjectReply, (client) => client.getProjects());

        projectResult.throwIfFailed();

        this.safeSetState((draft) => {
            draft.projectList = projectResult.reply!.data.map(project => (
                project.name
            ));
        });
    }
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MButton label="Add"
                         color="success"
                         icon={MNewIconName}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.new_Click}/>
                <MSpacer/>
                <MButton label="Import"
                         color="success"
                         icon={ExcelIconName}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.import_Click}/>
                <MSpacer/>
                <MButton label="Export"
                         icon={ExcelIconName}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.export_Click}/>
                <MSpacer/>
                <MButton label="Open"
                         icon={MOpenIconName}
                         iconOnly={false}
                         disabled={this.isOpenButtonDisabled}
                         onClick={this.open_Click}/>
                <MSpacer/>
                <MButton label={this.state.includeLabel}
                         icon={MAllowIconName}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.includeComplete_Click}/>
                <MSpacer/>
                <MButton label="Notices"
                         color="help"
                         icon={GotoIconName}
                         iconOnly={false}
                         disabled={false}
                         onClick={this.notices_Click}/>
            </MToolbar>
        );

    }

    private receivedDateTemplate(rowData: SafeDigRow): string | undefined {
        if(rowData.data.receivedDate != null) {
            try {
                return rowData.data.receivedDate!.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    }

    private requiredDateTemplate = (rowData: SafeDigRow) => {
        if(rowData.data.requiredDate != null) {
            try {
                return rowData.data.requiredDate!.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    };

    private expiryDateTemplate = (rowData: SafeDigRow) => {
        if(rowData.data.expiryDate != null) {
            try {
                return rowData.data.expiryDate!.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    };

    private uploadDateTemplate = (rowData: SafeDigRow) => {
        if(rowData.data.uploadDate != null) {
            try {
                return rowData.data.uploadDate!.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    };

    private supervisionDateTemplate = (rowData: SafeDigRow) => {
        if(rowData.data.supervisionDate != null) {
            try {
                return rowData.data.supervisionDate!.toLocaleDateString();
            } catch {
                return '';
            }
        }
        return '';
    };

    private dateFilterTemplate = (options: any) => {
        return <Calendar value={options.value}
                         onChange={(e: any) => options.filterApplyCallback(e.value, options.index)}
                         dateFormat="dd/mm/yy"
                         placeholder="dd/mm/yyyy"
                         mask="99/99/9999"/>;
    };

    private statusTemplate(rowData: SafeDigRow): string {
        if(rowData.data.hotJob && rowData.data.supervisionDate !== null) {
            if(rowData.data.supervisionDate > new Date() && rowData.data.digStatus === 3) {
                return DigStatusNames[rowData.data.digStatus] + ' - ' + rowData.data.supervisionDate.toLocaleDateString();
            }
        }
        return DigStatusNames[rowData.data.digStatus];
    }

    private hotJobTemplate(rowData: SafeDigRow): ReactElement {
        return rowData.data.hotJob ? <img src={skullLogo} alt="HJ" /> : <MSpacer/>;
    }

    private lsbudStatusTemplate(rowData: SafeDigRow): ReactElement {
        return <StatusIndicator size={25} status={rowData.data.lsbudStatus}/>;
    }

    private elecStatusTemplate(rowData: SafeDigRow): ReactElement {
        return <StatusIndicator size={25} status={rowData.data.electricStatus}/>;
    }

    private gasStatusTemplate(rowData: SafeDigRow): ReactElement {
        return <StatusIndicator size={25} status={rowData.data.gasStatus}/>;
    }

    private waterStatusTemplate(rowData: SafeDigRow): ReactElement {
        return <StatusIndicator size={25} status={rowData.data.waterStatus}/>;
    }

    private bapaStatusTemplate(rowData: SafeDigRow): ReactElement {
        return <StatusIndicator size={25} status={rowData.data.bapaStatus}/>;
    }

    private telecomsStatusTemplate(rowData: SafeDigRow): ReactElement {
        return <StatusIndicator size={25} status={rowData.data.telecomsStatus}/>;
    }

    private noticeStatusTemplate(rowData: SafeDigRow): ReactElement {
        const notRequired = (rowData.data.noticeCount ?? 0) === 0;
        const validPermitCount = rowData.data.validPermits ?? 0;
        const completedPermitCount = rowData.data.completedPermits ?? 0;
        const status: number = completedPermitCount > 0 ? completedPermitCount === validPermitCount ? 2 : 6 : 1;

        return <StatusIndicator size={25} status={status} notRequired={notRequired}/>;
    }

    private getDigStatusFilterTemplate(options: ColumnFilterElementTemplateOptions): ReactNode {

        return (
            <MMultiDropDown id="safedigStatusFilter"
                            items={this.state.safeDigStatusValues}
                            disabled={false}
                            onChange={(selection: number[]) => options.filterApplyCallback(selection)}/>
        );

    }

    public render(): ReactElement {
        return (
            <MPage title="SafeDigs"
                   locked={this.state.page.locked}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>
                <ImportDialog ref={this.m_importRef}/>
                <MessageDialog ref={this.m_messageRef}/>

                <MTable innerRef={this.m_datatableRef}
                    rowKey="data.estimate"
                    rowData={this.state.table.rowData}
                    filters={this.state.table.filters}
                    onSelectionChange={this.table_onSelectionChange}
                        exportFunction={this.exportFunction}
                    sortMode={'multiple'}
                    multiSortMeta={[{field: 'data.estimate', order: 1}]}
                        exportName={'SafeDigsExport'}>

                    <Column field="data.estimate"
                            header="ID"
                            filter={true}
                            sortable={true}
                            style={{width: '12%'}}/>
                    <Column field="data.project"
                            header="Project"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.requestor"
                            header="Requestor"
                            filter={true}
                            sortable={true}
                            style={{width: '13%'}}/>
                    <Column field="data.workDescription"
                            header="Work Description"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.easting"
                            header="Easting"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.northing"
                            header="Northing"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.easting2"
                            header="Easting 2"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.northing2"
                            header="Northing 2"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.address1"
                            header="Address 1"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.address2"
                            header="Address 2"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.address3"
                            header="Address 3"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.county"
                            header="Build Area"
                            filter={true}
                            sortable={true}
                            style={{width: '12%'}}/>
                    <Column field="data.postcode"
                            header="Postcode"
                            filter={true}
                            sortable={true}
                            style={{width: '13%'}}/>
                    <Column field="data.receivedDate"
                            header="Received Date"
                            dataType="date"
                            filter={true}
                            filterElement={this.dateFilterTemplate}
                            sortable={true}
                            body={this.receivedDateTemplate}
                            style={{width: '12%'}}/>
                    <Column field="data.requiredDate"
                            header="Required Date"
                            dataType="date"
                            filter={true}
                            filterElement={this.dateFilterTemplate}
                            sortable={true}
                            body={this.requiredDateTemplate}
                            style={{width: '13%'}}/>
                    <Column field="data.digStatus"
                            header="Status"
                            filter={true}
                            filterElement={this.getDigStatusFilterTemplate}
                            showFilterMenu={false}
                            showClearButton={false}
                            sortable={true}
                            body={this.statusTemplate}
                            style={{width: '10%'}}/>
                    <Column field="data.expiryDate"
                            header="LSBUD Expiry"
                            dataType="date"
                            filter={true}
                            filterElement={this.dateFilterTemplate}
                            sortable={true}
                            body={this.expiryDateTemplate}
                            style={{width: '13%'}}/>
                    <Column field="data.hotJob"
                            header="Hot"
                            filter={false}
                            body={this.hotJobTemplate}
                            style={{width: '5%'}}/>
                    <Column field="data.quarantine"
                            header="Quarantine"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.supervisionDate"
                            header="Supervision Date"
                            filter={true}
                            sortable={true}
                            dataType="date"
                            body={this.supervisionDateTemplate}
                            style={{display:"none"}}/>
                    <Column field="data.uploadUser"
                            header="LSBUDUser"
                            filter={true}
                            sortable={true}
                            style={{display:"none"}}/>
                    <Column field="data.uploadDate"
                            header="LSBUDDate"
                            dataType="date"
                            filter={true}
                            sortable={true}
                            body={this.uploadDateTemplate}
                            style={{display:"none"}}/>
                    <Column field="data.lsbudStatus"
                            header="LSBUD"
                            filter={false}
                            body={this.lsbudStatusTemplate}
                            style={{width: '5%'}}/>
                    <Column field="data.electricStatus"
                            header="Electric"
                            filter={false}
                            body={this.elecStatusTemplate}
                            style={{width: '5%'}}/>
                    <Column field="data.gasStatus"
                            header="Gas"
                            body={this.gasStatusTemplate}
                            filter={false}
                            style={{width: '5%'}}/>
                    <Column field="data.waterStatus"
                            header="Water"
                            body={this.waterStatusTemplate}
                            filter={false}
                            style={{width: '5%'}}/>
                    <Column field="data.bapaStatus"
                            header="BAPA"
                            body={this.bapaStatusTemplate}
                            filter={false}
                            style={{width: '5%'}}/>
                    <Column field="data.telecomsStatus"
                            header="Telecoms"
                            body={this.telecomsStatusTemplate}
                            filter={false}
                            style={{width: '5%'}}/>
                    <Column header="Notices"
                            headerTooltip={'Valid Permits / Completed Permits / Notices'}
                            body={this.noticeStatusTemplate}
                            style={{width: '5%'}}/>
                </MTable>
            </MPage>
        );
    }
}
export default withMsal(WithRouter(ListSafeDigsPage));